import React, { useCallback, useEffect, useState } from "react";
import GalleryModal from "./GalleryModal";
import GalleryNav from "./GalleryNav";
import useGalleryNavigation from "./useGalleryNavigation";
import "./Gallery.scss";

function normalizeIndex(index, length) {
  if (length <= 0) {
    return 0;
  }

  return ((index % length) + length) % length;
}

export default function Gallery({
  isOpen,
  items,
  onClose,
  initialIndex = 0,
  loop = false,
  keyboard = true,
  swipe = true,
}) {
  const safeItems = Array.isArray(items) ? items : [];
  const itemsCount = safeItems.length;
  const hasMultipleItems = itemsCount > 1;
  const lastIndex = itemsCount - 1;

  const [currentIndex, setCurrentIndex] = useState(() =>
    normalizeIndex(initialIndex, itemsCount),
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCurrentIndex(normalizeIndex(initialIndex, itemsCount));
  }, [initialIndex, isOpen, itemsCount]);

  const goPrev = useCallback(() => {
    if (!hasMultipleItems) {
      return;
    }

    setCurrentIndex((prev) => {
      if (prev === 0) {
        return loop ? lastIndex : 0;
      }

      return prev - 1;
    });
  }, [hasMultipleItems, lastIndex, loop]);

  const goNext = useCallback(() => {
    if (!hasMultipleItems) {
      return;
    }

    setCurrentIndex((prev) => {
      if (prev === lastIndex) {
        return loop ? 0 : lastIndex;
      }

      return prev + 1;
    });
  }, [hasMultipleItems, lastIndex, loop]);

  const { onTouchStart, onTouchEnd } = useGalleryNavigation({
    isOpen,
    keyboard,
    swipe,
    onClose,
    onNext: goNext,
    onPrev: goPrev,
  });

  const currentItem = safeItems[currentIndex];

  const handleBackdropAreaClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose?.();
      }
    },
    [onClose],
  );

  return (
    <GalleryModal isOpen={isOpen} onClose={onClose}>
      <div
        className="gallery"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={handleBackdropAreaClick}
      >
        {currentItem ? (
          <img
            className="gallery-image"
            src={currentItem.src}
            alt={currentItem.alt || "Изображение проекта"}
            data-testid="gallery-image"
            data-gallery-index={currentIndex}
          />
        ) : (
          <div className="gallery-empty" data-testid="gallery-empty">
            Нет изображений
          </div>
        )}

        {hasMultipleItems && <GalleryNav onPrev={goPrev} onNext={goNext} />}
      </div>
    </GalleryModal>
  );
}
